import React from "react";
import { useDispatch } from "react-redux";
import { NumericFormat } from "react-number-format";
import {
  FormWrapper,
  CauseAreaTitle,
  CauseAreaContext,
  TotalSumWrapper,
  SumWrapper,
  SumButtonsWrapper,
} from "../AmountPane.style";

import { getCauseAreaIconById } from "../SelectionPane.style";
import { setCauseAreaAmount } from "../../../store/donation/actions";
import { usePlausible } from "next-plausible";
import { EffektButton, EffektButtonVariant } from "../../../../EffektButton/EffektButton";
import { thousandize } from "../../../../../../../util/formatting";
import { CauseArea } from "../../../types/CauseArea";
import { CauseAreaDisplayConfig, OperationsConfig } from "../../../types/WidgetProps";
import { InfoAccordion } from "../../shared/InfoAccordion/InfoAccordion";

interface OperationsCauseAreaFormProps {
  causeArea: CauseArea;
  suggestedSums: Array<{ amount: number; subtext?: string }>;
  causeAreaAmounts: Record<number, number>;
  causeAreaDisplayConfig: CauseAreaDisplayConfig;
  operationsConfig?: OperationsConfig;
}

export const OperationsCauseAreaForm: React.FC<OperationsCauseAreaFormProps> = ({
  causeArea,
  suggestedSums,
  causeAreaAmounts,
  causeAreaDisplayConfig,
  operationsConfig,
}) => {
  const dispatch = useDispatch<any>();
  const plausible = usePlausible();

  return (
    <FormWrapper>
      <div>
        <CauseAreaTitle>
          {getCauseAreaIconById(causeArea.id)}
          {causeArea.widgetDisplayName || causeArea.name}
        </CauseAreaTitle>
        <CauseAreaContext>
          {getCauseAreaContext(causeArea.id, causeAreaDisplayConfig)}
        </CauseAreaContext>
      </div>
      <div>
        <TotalSumWrapper>
          <SumButtonsWrapper>
            {suggestedSums.map((suggested) => (
              <div key={suggested.amount}>
                <EffektButton
                  variant={EffektButtonVariant.SECONDARY}
                  selected={causeAreaAmounts[causeArea.id] === suggested.amount}
                  onClick={() => {
                    plausible("SelectSuggestedSum", { props: { sum: suggested.amount } });
                    dispatch(setCauseAreaAmount(causeArea.id, suggested.amount));
                  }}
                  noMinWidth={true}
                >{`${suggested.amount ? thousandize(suggested.amount) : "-"} kr`}</EffektButton>
                {suggested.subtext && <i>{suggested.subtext}</i>}
              </div>
            ))}
          </SumButtonsWrapper>
          <SumWrapper>
            <span>
              <NumericFormat
                name="sum-operations"
                type="tel"
                placeholder="0"
                thousandSeparator=" "
                value={causeAreaAmounts[causeArea.id] > 0 ? causeAreaAmounts[causeArea.id] : ""}
                autoComplete="off"
                data-cy="donation-sum-input-operations"
                onValueChange={(values) => {
                  const v = values.floatValue === undefined ? 0 : values.floatValue;
                  dispatch(setCauseAreaAmount(causeArea.id, v));
                }}
                allowNegative={false}
                decimalScale={0}
              />
            </span>
          </SumWrapper>
        </TotalSumWrapper>
        {operationsConfig?.x_factor_info?.label_text && (
          <InfoAccordion
            labelText={operationsConfig.x_factor_info.label_text}
            description={operationsConfig.x_factor_info.description}
            link={operationsConfig.x_factor_info.link}
          />
        )}
      </div>
    </FormWrapper>
  );
};

const getCauseAreaContext = (id: number, config?: CauseAreaDisplayConfig) => {
  const context = config?.cause_area_contexts?.find((c) => c.cause_area_id === id);
  return context?.context_text || null;
};
